import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowRight, FileText, GitCommitHorizontal } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MaskedField } from '../../../lib/access';
import { programRef } from '../../../lib/access/types';
import { formatUtcYmd } from '../_shared/api/nullable';
import { formatMoney } from '../_shared/lib/recordLabels';
import { useProgramDocuments, useProgramModifications } from './hooks';
const tsEpoch = (v)=>{
    if (!v) return 0;
    const seconds = typeof v.seconds === 'bigint' ? Number(v.seconds) : v.seconds;
    return seconds;
};
// Every date on the spine is date-only and stored at UTC midnight — an SF-30
// effective date, a doc date — so it is formatted in UTC (formatUtcYmd), not
// converted to the reader's zone. Read through the local formatter, a
// 2026-05-13 00:00+00 mod renders as 2026-05-12 for every viewer west of UTC.
// The shared local helper is left alone; other surfaces hold true local
// instants and want it.
// A modification that states no effective date, which the reviewer left
// blank (D98): it stays undated, never the day it was applied.
export const UNDATED_MOD_LABEL = 'Effective date not stated';
// Pull a mod number ("P00008") out of a document title like "Modification P00008".
const modNumberFromTitle = (title)=>title.match(/\b([A-Z]\d{4,5})\b/i)?.[1]?.toUpperCase();
// Codepoint order, not localeCompare: the ordering of a timeline must not
// depend on the reader's locale or the host's ICU build.
const cmpText = (a, b)=>a < b ? -1 : a > b ? 1 : 0;
// The lineage order: contract anchors first, then modifications by effective
// date, undated ones last within their kind.
//
// It is a TOTAL order on purpose. Every branch resolves to the opposite sign
// when its arguments swap — including the both-undated pair, which used to
// answer 1 either way and left the engine's sort free to render the same data
// in different orders. Undated nodes fall through to the mod number and then
// to the node key (a document/registry id, unique per node), so ties are broken
// by something the caller can predict rather than by input order.
export const compareLineageNodes = (a, b)=>{
    if (a.kind !== b.kind) return a.kind === 'contract' ? -1 : 1;
    // 0 is the undated sentinel the node carries, not "before 1970".
    const aDated = a.epoch !== 0;
    const bDated = b.epoch !== 0;
    if (aDated !== bDated) return aDated ? -1 : 1;
    if (aDated && a.epoch !== b.epoch) return a.epoch - b.epoch;
    return cmpText(a.modNumber ?? '', b.modNumber ?? '') || cmpText(a.key, b.key);
};
// Merge program_documents (file link + dates) with program_modifications (the
// funding/scope deltas) into one ordered timeline: base contract first, then
// every modification by effective date. Read-time assembly — mirrors the
// field-lineage pattern; no stored chain.
export function buildLineage(docs, mods) {
    // The registry is append-only (re-applying a mod supersedes the prior row);
    // the timeline enriches from the LIVE row per number.
    const modByNumber = new Map();
    for (const m of mods){
        if (!m.modificationNumber) continue;
        const key = m.modificationNumber.toUpperCase();
        const existing = modByNumber.get(key);
        if (!existing || existing.status !== 'applied' && m.status === 'applied') {
            modByNumber.set(key, m);
        }
    }
    const nodes = [];
    const seenMods = new Set();
    // Base contract document(s).
    for (const d of docs.filter((x)=>x.category === 'Contract')){
        nodes.push({
            key: d.id,
            kind: 'contract',
            title: d.title || 'Contract',
            fileName: d.fileName,
            epoch: tsEpoch(d.docDate),
            dateLabel: (d.docDate ? formatUtcYmd(d.docDate) : undefined) ?? '—'
        });
    }
    // Modification documents — the file-linked nodes, enriched by a matching mod.
    //
    // The registry's effective_date wins over the document's doc_date, and the
    // two are the same date whenever both exist: pangaea dates the registry row
    // from what the modification states (the date read off the SF-30, or the
    // reviewer's correction), never from the apply instant (K9b-1). They can only
    // disagree once someone edits the Docs row afterwards, and then the registry
    // is the one to show — it is the transactional record of the applied
    // modification, it is what every other delivery surface (the mods list, the
    // funding rollups) reads, and a timeline that contradicts them is worse than
    // one that contradicts a file card. doc_date still carries the
    // staged-but-never-applied mod, which has no registry row to date it. A mod
    // whose date was left blank reads as not stated.
    for (const d of docs.filter((x)=>x.category === 'Modification')){
        const num = modNumberFromTitle(d.title);
        const m = num ? modByNumber.get(num) : undefined;
        if (num) seenMods.add(num);
        nodes.push({
            key: d.id,
            kind: 'modification',
            title: d.title || 'Modification',
            modNumber: num,
            fileName: d.fileName,
            epoch: tsEpoch(m?.effectiveDate) || tsEpoch(d.docDate),
            dateLabel: (m?.effectiveDate ? formatUtcYmd(m.effectiveDate) : undefined) ?? (d.docDate ? formatUtcYmd(d.docDate) : undefined) ?? UNDATED_MOD_LABEL,
            purpose: m?.purpose,
            modType: m?.modificationType,
            fundingImpact: m?.totalFundingImpact,
            noCost: m?.noCostModification
        });
    }
    // Modifications recorded without a linked document (e.g. the legacy cross-doc
    // path) — still belong on the timeline so nothing is silently dropped.
    for (const m of mods){
        const num = (m.modificationNumber || '').toUpperCase();
        if (num && seenMods.has(num)) continue;
        nodes.push({
            key: m.id,
            kind: 'modification',
            title: m.modificationNumber ? `Modification ${m.modificationNumber}` : 'Modification',
            modNumber: m.modificationNumber || undefined,
            epoch: tsEpoch(m.effectiveDate),
            dateLabel: (m.effectiveDate ? formatUtcYmd(m.effectiveDate) : undefined) ?? UNDATED_MOD_LABEL,
            purpose: m.purpose,
            modType: m.modificationType,
            fundingImpact: m.totalFundingImpact,
            noCost: m.noCostModification
        });
    }
    return nodes.sort(compareLineageNodes);
}
export function LineageTab({ programId }) {
    const { data: docsData, isLoading: docsLoading } = useProgramDocuments(programId);
    const { data: modsData, isLoading: modsLoading } = useProgramModifications(programId);
    const isLoading = docsLoading || modsLoading;
    const nodes = buildLineage(docsData?.items ?? [], modsData?.items ?? []);
    if (isLoading) {
        return /*#__PURE__*/ _jsx("p", {
            className: "text-sm text-muted-foreground",
            children: "Loading lineage…"
        });
    }
    if (nodes.length === 0) {
        return /*#__PURE__*/ _jsx("p", {
            className: "text-sm text-muted-foreground",
            children: "No contract documents linked yet. The original award and each modification appear here once they're processed."
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start justify-between gap-4",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "The document history of this contract — the original award, then each modification in effect order. Open the contract workspace to see a mod's before→after field changes."
                    }),
                    /*#__PURE__*/ _jsxs(Link, {
                        to: `/delivery/programs/${programId}/v2?screen=modifications`,
                        className: `${specraButtonVariants({
                            variant: 'secondary',
                            size: 'sm'
                        })} shrink-0`,
                        children: [
                            "Review changes",
                            /*#__PURE__*/ _jsx(ArrowRight, {
                                className: "ml-1 h-3.5 w-3.5"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("ol", {
                className: "relative ml-3 border-l border-border",
                "data-testid": Testids.deliveryDocs.lineageTimeline,
                children: nodes.map((n)=>/*#__PURE__*/ _jsxs("li", {
                        className: "relative ml-6 pb-6 last:pb-0",
                        "data-testid": Testids.deliveryDocs.lineageNode,
                        "data-kind": n.kind,
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full bg-background ring-1 ring-border",
                                children: n.kind === 'contract' ? /*#__PURE__*/ _jsx(FileText, {
                                    className: "h-3.5 w-3.5 text-primary"
                                }) : /*#__PURE__*/ _jsx(GitCommitHorizontal, {
                                    className: "h-3.5 w-3.5 text-muted-foreground"
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-baseline gap-x-2 gap-y-0.5",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "font-medium",
                                        children: n.title
                                    }),
                                    n.kind === 'contract' && /*#__PURE__*/ _jsx("span", {
                                        className: "rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary",
                                        children: "Base award"
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "text-sm text-muted-foreground",
                                        children: [
                                            "· ",
                                            n.dateLabel
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground",
                                children: [
                                    n.fileName && /*#__PURE__*/ _jsxs("span", {
                                        className: "inline-flex items-center gap-1",
                                        children: [
                                            /*#__PURE__*/ _jsx(FileText, {
                                                className: "h-3.5 w-3.5"
                                            }),
                                            n.fileName
                                        ]
                                    }),
                                    n.modType && /*#__PURE__*/ _jsx("span", {
                                        children: n.modType
                                    }),
                                    typeof n.fundingImpact === 'number' && n.fundingImpact !== 0 && /*#__PURE__*/ _jsxs("span", {
                                        children: [
                                            "Funding impact:",
                                            ' ',
                                            /*#__PURE__*/ _jsx(MaskedField, {
                                                fieldName: "total_funding_impact",
                                                resource: programRef(programId),
                                                placeholder: "Restricted",
                                                children: formatMoney(n.fundingImpact)
                                            })
                                        ]
                                    }),
                                    n.noCost && /*#__PURE__*/ _jsx("span", {
                                        children: "No-cost"
                                    })
                                ]
                            }),
                            n.purpose && /*#__PURE__*/ _jsx("p", {
                                className: "mt-1 text-sm text-foreground/80",
                                children: n.purpose
                            })
                        ]
                    }, n.key))
            })
        ]
    });
}
